export type DashboardApp =
  | "accesspoint"
  | "perkpoint"
  | "gamepoint"
  | "xpandpoint"
  | "rewardpoint"
  | "giftcard"
  | "offers";

/** Legacy root paths that still render under Access Point navigation. */
const LEGACY_ACCESS_POINT_PREFIXES = [
  "/dashboard",
  "/programs",
  "/merchants",
  "/transactions",
  "/invoices",
  "/analytics",
  "/jobs",
  "/clients",
  "/users",
  "/settings",
];

/** API `applicationAccess` codes that grant a dashboard app */
const APP_ACCESS_CODES: Record<DashboardApp, readonly string[]> = {
  accesspoint: ["ACCESS_POINT"],
  xpandpoint: ["XPAND_POINT"],
  rewardpoint: ["REWARD_POINT"],
  perkpoint: ["PERK_POINT"],
  giftcard: ["GIFT_CARD"],
  offers: ["OFFER_POINT", "OFFERS"],
  // Game Point is separate from Perk Point — do not treat PERK_POINT as access.
  gamepoint: ["GAMIFICATION", "GAME_POINT"],
};

const ACCESS_CODE_TO_HOME: Record<string, string> = {
  ACCESS_POINT: "/accesspoint",
  XPAND_POINT: "/xpandpoint",
  REWARD_POINT: "/rewardpoint",
  PERK_POINT: "/perkpoint",
  GIFT_CARD: "/giftcard",
  OFFER_POINT: "/offers",
  OFFERS: "/offers",
  GAMIFICATION: "/gamepoint",
  GAME_POINT: "/gamepoint",
};

/** App-switcher tile ids → dashboard app keys */
const SWITCHER_ID_TO_APP: Record<string, DashboardApp> = {
  "access-point": "accesspoint",
  "xpand-point": "xpandpoint",
  "reward-point": "rewardpoint",
  "perk-point": "perkpoint",
  "gift-card": "giftcard",
  offers: "offers",
  "game-point": "gamepoint",
};

export function getAppFromPathname(pathname: string): DashboardApp | null {
  if (pathname === "/" || pathname.startsWith("/accesspoint")) {
    return "accesspoint";
  }
  if (pathname.startsWith("/gamepoint")) return "gamepoint";
  if (pathname.startsWith("/perkpoint")) return "perkpoint";
  if (pathname.startsWith("/xpandpoint")) return "xpandpoint";
  if (pathname.startsWith("/rewardpoint")) return "rewardpoint";
  if (pathname.startsWith("/giftcard")) return "giftcard";
  if (pathname.startsWith("/offers")) return "offers";

  if (
    LEGACY_ACCESS_POINT_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    )
  ) {
    return "accesspoint";
  }

  return null;
}

export function isKnownAppRoute(pathname: string): boolean {
  return getAppFromPathname(pathname) !== null;
}

export function getStoredApplicationAccess(): string[] {
  try {
    const raw =
      sessionStorage.getItem("userInfo") || localStorage.getItem("userInfo");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.applicationAccess)
      ? parsed.applicationAccess
      : [];
  } catch {
    return [];
  }
}

export function canAccessApp(
  app: DashboardApp | null,
  accessList: string[] = getStoredApplicationAccess(),
): boolean {
  if (!app) return true;
  const codes = APP_ACCESS_CODES[app];
  return codes.some((code) => accessList.includes(code));
}

export function canAccessPathname(
  pathname: string,
  accessList: string[] = getStoredApplicationAccess(),
): boolean {
  return canAccessApp(getAppFromPathname(pathname), accessList);
}

/** Home path for the first app in the user's `applicationAccess` list. */
export function getFirstAllowedAppHome(
  accessList: string[] = getStoredApplicationAccess(),
): string {
  for (const code of accessList) {
    const home = ACCESS_CODE_TO_HOME[code];
    if (home) return home;
  }
  return "/login";
}

/** Whether an app-switcher tile id is allowed for this access list. */
export function canAccessSwitcherAppId(
  switcherAppId: string,
  accessList: string[] = getStoredApplicationAccess(),
): boolean {
  const app = SWITCHER_ID_TO_APP[switcherAppId];
  if (!app) return false;
  return canAccessApp(app, accessList);
}
